import React, { useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Tab,
  Tabs,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ButtonGroup,
  TextField,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ViewModule as TemplateIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  RotateRight as RotateIcon,
  SelectAll as SelectIcon,
} from '@mui/icons-material'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../store'
import {
  setSelectedTemplate,
  setTemplateCategory,
  loadTemplates,
  rotateTemplate,
  startSelection,
  openEventEditDialog,
} from '../store/editorSlice'
import { placeTemplate, updateDungeonProperties } from '../store/mapSlice'
import { Template, TemplateCategory, EventPlacementType } from '../types/map'
import { presetTemplates, getCategoryDisplayName } from '../data/presetTemplates'
import EventTemplateDialog from './EventTemplateDialog'
import { EventTemplate } from '../data/eventTemplates'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ height: '100%' }}>
      {value === index && <Box sx={{ height: '100%' }}>{children}</Box>}
    </div>
  )
}

// ヘルパー関数
const getFloorTypeColor = (floorType: string, customFloorTypes: any[]) => {
  // カスタム床タイプかどうかを確認
  const customFloor = customFloorTypes.find(custom => custom.id === floorType)
  if (customFloor) {
    return customFloor.color
  }
  
  switch (floorType) {
    case 'normal': return '#666'
    case 'damage': return '#a44'
    case 'slippery': return '#47a'
    case 'pit': return '#222'
    case 'warp': return '#a74'
    default: return '#666'
  }
}

const getFloorTypeName = (floorType: string, customFloorTypes: any[]) => {
  // カスタム床タイプかどうかを確認
  const customFloor = customFloorTypes.find(custom => custom.id === floorType)
  if (customFloor) {
    return customFloor.name
  }
  
  switch (floorType) {
    case 'normal': return '通常'
    case 'damage': return 'ダメージ'
    case 'slippery': return '滑りやすい'
    case 'pit': return '落とし穴'
    case 'warp': return 'ワープ'
    default: return '不明'
  }
}

const getWallTypeColor = (wallType: string, customWallTypes: any[]) => {
  // カスタム壁タイプかどうかを確認
  const customWall = customWallTypes.find(custom => custom.id === wallType)
  if (customWall) {
    return customWall.color
  }
  
  switch (wallType) {
    case 'normal': return '#ffffff'
    case 'door': return '#D2691E'
    case 'locked_door': return '#FFD700'
    case 'hidden_door': return '#999999'
    case 'breakable': return '#FF8C42'
    case 'oneway': return '#40E0D0'
    case 'invisible': return '#777777'
    case 'event': return '#FF69B4'
    default: return '#ffffff'
  }
}

const getWallTypeName = (wallType: string, customWallTypes: any[]) => {
  // カスタム壁タイプかどうかを確認
  const customWall = customWallTypes.find(custom => custom.id === wallType)
  if (customWall) {
    return customWall.name
  }
  
  switch (wallType) {
    case 'normal': return '通常壁'
    case 'door': return '扉'
    case 'locked_door': return '鍵付き扉'
    case 'hidden_door': return '隠し扉'
    case 'breakable': return '破壊可能壁'
    case 'oneway': return '片面壁'
    case 'invisible': return '透明壁'
    case 'event': return 'イベント壁'
    default: return '不明'
  }
}

const getDirectionName = (direction: string) => {
  switch (direction) {
    case 'north': return '北'
    case 'east': return '東'
    case 'south': return '南'
    case 'west': return '西'
    default: return direction
  }
}

const getDecorationIcon = (decorationType: string) => {
  switch (decorationType) {
    case 'furniture': return '🪑'
    case 'statue': return '🗿'
    case 'plant': return '🌿'
    case 'torch': return '🔥'
    case 'pillar': return '🏛️'
    case 'rug': return '🧿'
    case 'painting': return '🖼️'
    case 'crystal': return '💎'
    case 'rubble': return '🪨'
    default: return '❓'
  }
}

const RightPanel: React.FC = () => {
  const dispatch = useDispatch()
  const { templates, selectedTemplate, templateCategory, templateRotation, currentFloor, hoveredCellInfo, customFloorTypes, customWallTypes } = useSelector(
    (state: RootState) => state.editor
  )
  const dungeon = useSelector((state: RootState) => state.map.dungeon)

  const [tabValue, setTabValue] = React.useState(0) // 初期状態でプロパティタブを選択（位置変更後）
  const [showEventTemplateDialog, setShowEventTemplateDialog] = React.useState(false)

  // プリセットテンプレートをロード
  useEffect(() => {
    dispatch(loadTemplates(presetTemplates))
  }, [dispatch])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleCategoryChange = (category: TemplateCategory) => {
    dispatch(setTemplateCategory(category))
  }

  const handleTemplateSelect = (template: Template) => {
    // 既に選択されているテンプレートを再度クリックした場合は選択解除
    if (selectedTemplate && selectedTemplate.id === template.id) {
      dispatch(setSelectedTemplate(null))
    } else {
      dispatch(setSelectedTemplate(template))
    }
  }

  const handlePlaceTemplate = () => {
    if (!selectedTemplate) return
    
    // テンプレート配置モードを有効にする（マップ上でクリックして配置）
    console.log(`テンプレート「${selectedTemplate.name}」の配置モードを開始しました`)
  }

  const handleCreateTemplate = () => {
    dispatch(startSelection())
  }

  const handleEventTemplateSelect = (template: EventTemplate) => {
    if (template.presetEvent && dungeon) {
      const now = new Date().toISOString()
      const templateEvent = template.presetEvent
      
      // テンプレートから新しいイベントを作成
      const newEvent = {
        id: crypto.randomUUID(),
        type: templateEvent.type || 'custom',
        name: templateEvent.name || 'イベント',
        description: templateEvent.description || '',
        position: { x: 0, y: 0, placement: 'floor' as EventPlacementType }, // デフォルト位置
        appearance: {
          visible: true,
          ...templateEvent.appearance
        },
        trigger: templateEvent.trigger || { type: 'interact', repeatPolicy: { type: 'once' } },
        actions: templateEvent.actions ? [...templateEvent.actions] : [],
        properties: templateEvent.properties || {},
        enabled: templateEvent.enabled !== undefined ? templateEvent.enabled : true,
        priority: templateEvent.priority !== undefined ? templateEvent.priority : 1,
        flags: templateEvent.flags || {},
        metadata: {
          created: now,
          modified: now,
          author: dungeon.author || '',
          version: 1
        }
      }
      
      // イベント編集ダイアログを開く
      dispatch(openEventEditDialog(newEvent))
      setShowEventTemplateDialog(false)
    }
  }

  const categories: TemplateCategory[] = ['room', 'corridor', 'junction', 'trap', 'puzzle', 'decoration', 'fullmap', 'custom']

  const filteredTemplates = templates.filter(
    template => template.category === templateCategory
  )

  // テンプレートサムネイル描画コンポーネント
  const TemplateThumbnail: React.FC<{ template: Template; width: number; height: number }> = ({ template, width, height }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // キャンバスサイズを設定
      canvas.width = width
      canvas.height = height

      // 背景をクリア
      ctx.fillStyle = '#1a1a1a'
      ctx.fillRect(0, 0, width, height)

      // テンプレートサイズ
      const templateWidth = template.size.width
      const templateHeight = template.size.height
      
      // セルサイズを計算（パディングを考慮）
      const padding = 4
      const cellSize = Math.min(
        (width - padding * 2) / templateWidth,
        (height - padding * 2) / templateHeight
      )

      // 描画開始位置を中央に調整
      const startX = (width - templateWidth * cellSize) / 2
      const startY = (height - templateHeight * cellSize) / 2

      // 各セルを描画
      for (let y = 0; y < templateHeight; y++) {
        for (let x = 0; x < templateWidth; x++) {
          const cell = template.cells[y][x]
          const xPos = startX + x * cellSize
          const yPos = startY + y * cellSize

          // 床の描画（サムネイル用に明度を調整、カスタム床タイプ対応）
          const floorColor = getFloorTypeColor(cell.floor.type, customFloorTypes)
          
          ctx.fillStyle = floorColor
          ctx.fillRect(xPos, yPos, cellSize, cellSize)

          // 壁の描画
          const drawWall = (direction: 'north' | 'east' | 'south' | 'west', wall: any) => {
            if (!wall) return
            
            let strokeColor = '#fff'
            let lineWidth = Math.max(1, cellSize / 8)

            switch (wall.type) {
              case 'normal': strokeColor = '#ffffff'; break
              case 'door': strokeColor = '#D2691E'; break
              case 'locked_door': strokeColor = '#FFD700'; break
              case 'hidden_door': strokeColor = '#999999'; break
              case 'breakable': strokeColor = '#FF8C42'; break
              case 'oneway': strokeColor = '#40E0D0'; break
              case 'invisible': strokeColor = '#777777'; break
              case 'event': strokeColor = '#FF69B4'; break
            }

            ctx.strokeStyle = strokeColor
            ctx.lineWidth = lineWidth

            ctx.beginPath()
            switch (direction) {
              case 'north':
                ctx.moveTo(xPos, yPos)
                ctx.lineTo(xPos + cellSize, yPos)
                break
              case 'east':
                ctx.moveTo(xPos + cellSize, yPos)
                ctx.lineTo(xPos + cellSize, yPos + cellSize)
                break
              case 'south':
                ctx.moveTo(xPos, yPos + cellSize)
                ctx.lineTo(xPos + cellSize, yPos + cellSize)
                break
              case 'west':
                ctx.moveTo(xPos, yPos)
                ctx.lineTo(xPos, yPos + cellSize)
                break
            }
            ctx.stroke()
          }

          drawWall('north', cell.walls.north)
          drawWall('east', cell.walls.east)
          drawWall('south', cell.walls.south)
          drawWall('west', cell.walls.west)

          // イベントの描画（小さなドット）
          if (cell.events.length > 0 && cellSize > 3) {
            ctx.fillStyle = '#ffdd44'
            ctx.beginPath()
            ctx.arc(xPos + cellSize * 0.75, yPos + cellSize * 0.25, Math.max(1.5, cellSize / 6), 0, Math.PI * 2)
            ctx.fill()
          }

          // 装飾の描画（小さなドット）
          if (cell.decorations.length > 0 && cellSize > 3) {
            ctx.fillStyle = '#aaaaaa'
            ctx.beginPath()
            ctx.arc(xPos + cellSize * 0.25, yPos + cellSize * 0.75, Math.max(1, cellSize / 8), 0, Math.PI * 2)
            ctx.fill()
          }
        }
      }

      // 外枠
      ctx.strokeStyle = '#666'
      ctx.lineWidth = 1
      ctx.strokeRect(startX - 1, startY - 1, templateWidth * cellSize + 2, templateHeight * cellSize + 2)

    }, [template, width, height])

    return <canvas ref={canvasRef} style={{ width: '100%', height: '100%', imageRendering: 'pixelated' }} />
  }

  return (
    <Box
      sx={{
        width: 320,
        height: '100%',
        bgcolor: 'background.paper',
        borderLeft: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth">
          <Tab 
            icon={<SettingsIcon />} 
            label="プロパティ" 
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<TemplateIcon />} 
            label="テンプレート" 
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {/* プロパティタブ */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  セルプロパティ
                  {hoveredCellInfo && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main' }}>
                      ({hoveredCellInfo.position.x}, {hoveredCellInfo.position.y})
                    </Typography>
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {hoveredCellInfo ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* 座標情報 */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        座標
                      </Typography>
                      <Typography variant="body2">
                        X: {hoveredCellInfo.position.x}, Y: {hoveredCellInfo.position.y}
                      </Typography>
                    </Box>

                    {/* 床情報 */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        床タイプ
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            bgcolor: getFloorTypeColor(hoveredCellInfo.floor.type, customFloorTypes),
                            border: '1px solid #ccc',
                            borderRadius: '2px',
                          }}
                        />
                        <Typography variant="body2">
                          {getFloorTypeName(hoveredCellInfo.floor.type, customFloorTypes)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        通行可否: {hoveredCellInfo.floor.passable ? '可能' : '不可'}
                      </Typography>
                    </Box>

                    {/* 壁情報 */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        壁情報
                      </Typography>
                      {(['north', 'east', 'south', 'west'] as const).map((direction) => {
                        const wall = hoveredCellInfo.walls[direction]
                        const directionName = getDirectionName(direction)
                        return (
                          <Box key={direction} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ minWidth: 40 }}>
                              {directionName}:
                            </Typography>
                            {wall ? (
                              <>
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    bgcolor: getWallTypeColor(wall.type, customWallTypes),
                                    border: '1px solid #ccc',
                                    borderRadius: '2px',
                                  }}
                                />
                                <Typography variant="body2">
                                  {getWallTypeName(wall.type, customWallTypes)}
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                なし
                              </Typography>
                            )}
                          </Box>
                        )
                      })}
                    </Box>

                    {/* イベント情報 */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        イベント ({hoveredCellInfo.events.length}個)
                      </Typography>
                      {hoveredCellInfo.events.length > 0 ? (
                        hoveredCellInfo.events.map((event, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                bgcolor: '#ffd700',
                                border: '1px solid #ccc',
                                borderRadius: '50%',
                              }}
                            />
                            <Typography variant="body2">
                              {event.name} ({event.type})
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          なし
                        </Typography>
                      )}
                    </Box>

                    {/* 装飾情報 */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        装飾 ({hoveredCellInfo.decorations.length}個)
                      </Typography>
                      {hoveredCellInfo.decorations.length > 0 ? (
                        hoveredCellInfo.decorations.map((decoration, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2" sx={{ fontSize: '12px' }}>
                              {getDecorationIcon(decoration.type)}
                            </Typography>
                            <Typography variant="body2">
                              {decoration.name} ({decoration.type})
                            </Typography>
                          </Box>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          なし
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    マウスをマップ上のセルに重ねると詳細情報が表示されます
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">マップ設定</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    マップ全体のカスタムプロパティを設定できます
                  </Typography>
                  
                  {/* マップカスタムプロパティ編集UI */}
                  {dungeon && (
                    <Box>
                      {Object.entries(dungeon.properties || {}).map(([key, value], index) => (
                        <Box key={`map-property-${index}`} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                          <TextField
                            label="キー"
                            value={key}
                            onChange={(e) => {
                              const newProperties = { ...dungeon.properties }
                              delete newProperties[key]
                              newProperties[e.target.value] = value
                              dispatch(updateDungeonProperties(newProperties))
                            }}
                            size="small"
                            sx={{ flex: 1 }}
                          />
                          <TextField
                            label="値"
                            value={typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            onChange={(e) => {
                              let parsedValue: any = e.target.value
                              // 数値判定
                              if (!isNaN(Number(e.target.value)) && e.target.value.trim() !== '') {
                                parsedValue = Number(e.target.value)
                              }
                              // JSON判定
                              else if (e.target.value.startsWith('{') || e.target.value.startsWith('[')) {
                                try {
                                  parsedValue = JSON.parse(e.target.value)
                                } catch {
                                  // JSON解析失敗時は文字列として扱う
                                }
                              }
                              // boolean判定
                              else if (e.target.value === 'true') {
                                parsedValue = true
                              } else if (e.target.value === 'false') {
                                parsedValue = false
                              }
                              
                              const newProperties = { ...dungeon.properties }
                              newProperties[key] = parsedValue
                              dispatch(updateDungeonProperties(newProperties))
                            }}
                            size="small"
                            sx={{ flex: 2 }}
                          />
                          <Button
                            size="small"
                            onClick={() => {
                              const newProperties = { ...dungeon.properties }
                              delete newProperties[key]
                              dispatch(updateDungeonProperties(newProperties))
                            }}
                            color="error"
                            sx={{ minWidth: 'auto', px: 1 }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                      
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newProperties = { ...dungeon.properties }
                          let newKey = 'newProperty'
                          let counter = 1
                          while (newKey in newProperties) {
                            newKey = `newProperty${counter}`
                            counter++
                          }
                          newProperties[newKey] = ''
                          dispatch(updateDungeonProperties(newProperties))
                        }}
                        size="small"
                        variant="outlined"
                        fullWidth
                      >
                        プロパティを追加
                      </Button>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </TabPanel>

        {/* テンプレートタブ */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            {/* カテゴリ選択 */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">
                  カテゴリ
                </Typography>
                <Button
                  size="small"
                  startIcon={<SelectIcon />}
                  onClick={handleCreateTemplate}
                  disabled={!dungeon}
                  sx={{ fontSize: '0.7rem' }}
                >
                  テンプレート作成
                </Button>
              </Box>
              <ButtonGroup size="small" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={templateCategory === category ? 'contained' : 'outlined'}
                    onClick={() => handleCategoryChange(category)}
                    size="small"
                    sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                  >
                    {getCategoryDisplayName(category)}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>

            {/* テンプレート一覧 */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
              <Grid container spacing={1}>
                {filteredTemplates.map((template) => (
                  <Grid item xs={12} key={template.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selectedTemplate?.id === template.id ? 2 : 1,
                        borderColor: selectedTemplate?.id === template.id 
                          ? 'primary.main' 
                          : 'divider',
                        '&:hover': {
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <Box sx={{ display: 'flex' }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 60,
                            border: 1,
                            borderColor: 'divider',
                            borderRadius: 1,
                            overflow: 'hidden',
                            flexShrink: 0,
                            position: 'relative',
                          }}
                        >
                          <TemplateThumbnail 
                            template={template} 
                            width={80} 
                            height={60} 
                          />
                          {/* サイズ表示をオーバーレイ */}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              bgcolor: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              px: 0.5,
                              py: 0.25,
                              fontSize: '0.6rem',
                              lineHeight: 1,
                            }}
                          >
                            {template.size.width}×{template.size.height}
                          </Box>
                        </Box>
                        <CardContent sx={{ flex: 1, p: 1, '&:last-child': { pb: 1 } }}>
                          <Typography variant="body2" fontWeight="bold" gutterBottom>
                            {template.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            {template.description}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                            {template.tags.slice(0, 2).map((tag) => (
                              <Chip
                                key={tag}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem', height: 16 }}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {filteredTemplates.length === 0 && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    このカテゴリにはテンプレートがありません
                  </Typography>
                </Box>
              )}
            </Box>

            {/* 選択されたテンプレートの詳細 */}
            {selectedTemplate && (
              <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  選択中: {selectedTemplate.name}
                </Typography>
                
                {/* 回転コントロール */}
                {!selectedTemplate.isFullMap && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="body2">回転:</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RotateIcon />}
                        onClick={() => {
                          console.log('回転ボタンクリック - 現在の角度:', templateRotation)
                          dispatch(rotateTemplate())
                        }}
                      >
                        {templateRotation}°
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Q/R/クリック
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        現在の回転角度: {templateRotation}° | Q=左回転 R=右回転
                      </Typography>
                    </Box>
                  </>
                )}
                
                {selectedTemplate.isFullMap ? (
                  <>
                    <Button
                      variant="contained"
                      color="warning"
                      startIcon={<AddIcon />}
                      fullWidth
                      disabled={!dungeon}
                      size="small"
                      onClick={() => {
                        if (selectedTemplate && dungeon) {
                          const confirmed = window.confirm('マップ全体が置き換えられます。現在のマップは失われますが、よろしいですか？')
                          if (confirmed) {
                            console.log(`マップ全体テンプレート「${selectedTemplate.name}」を適用します`)
                            dispatch(placeTemplate({
                              template: selectedTemplate,
                              floorIndex: currentFloor,
                              rotation: templateRotation
                            }))
                            // マップ全体置き換え後はテンプレート選択を解除
                            dispatch(setSelectedTemplate(null))
                          }
                        }
                      }}
                    >
                      マップ全体を置き換え
                    </Button>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: 'warning.main' }}>
                      注意: 現在のマップが完全に置き換えられます
                    </Typography>
                  </>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      fullWidth
                      disabled={!dungeon}
                      size="small"
                      onClick={handlePlaceTemplate}
                    >
                      クリックで配置
                    </Button>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                      マップ上の任意の位置をクリックして配置
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </TabPanel>
      </Box>

      {/* イベントテンプレートダイアログ */}
      <EventTemplateDialog
        open={showEventTemplateDialog}
        onClose={() => setShowEventTemplateDialog(false)}
        onSelectTemplate={handleEventTemplateSelect}
      />
    </Box>
  )
}

export default RightPanel